import { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import map from 'lodash/map';
import omit from 'lodash/omit';
import merge from 'lodash/merge';

import { captureException } from '@jerry-serverless/next-sentry';

import { getClient } from '../index';
import type { SearchPolicyDriver } from '../../types/policy';
import type { ViolationType } from '../../types/violation';

export const QUERY_DRIVER_RISK = gql`
  query GetDriverRisks($driver: DriverRiskDriverInput!) {
    getDriverRisks(driver: $driver) {
      dateReport
      description
      descriptionText
      points
      mvrOrderID
      type
    }
  }
`;

export interface DriverRiskDriverInput {
  id?: string;
  DLNum?: string;
  DLState?: string;
  dob: string;
  name: string;
}

export interface DriverRiskInput {
  driver: DriverRiskDriverInput;
}

/**
 * Query Driver Violations hook
 */
export const useQueryDriverRisks = (): [
  boolean,
  (drivers: SearchPolicyDriver[]) => Promise<Record<string, ViolationType[]>>,
] => {
  const [isLoading, setIsLoading] = useState(false);
  const client = getClient();

  const queryDriverRisk = useCallback(
    async (driver: SearchPolicyDriver): Promise<Record<string, ViolationType[]>> => {
      try {
        const resp = await client.query<{ getDriverRisks: ViolationType[] }, DriverRiskInput>({
          query: QUERY_DRIVER_RISK,
          variables: {
            driver: {
              id: driver.id,
              dob: driver.dateOfBirth ?? '',
              name: driver.name,
            },
          },
        });
        const violations = map(resp.data.getDriverRisks, (violation) => ({
          ...omit(violation, '__typename'),
          approved: true,
        }));
        return { [driver.id]: violations };
      } catch (error) {
        captureException(error);
        return {};
      }
    },
    [client]
  );

  const queryDriverRisks = useCallback(
    async (drivers: SearchPolicyDriver[]) => {
      setIsLoading(true);
      const results = await Promise.all(drivers.map(queryDriverRisk));
      setIsLoading(false);
      return merge({}, ...results);
    },
    [setIsLoading, queryDriverRisk]
  );

  return [isLoading, queryDriverRisks];
};
